/**
 * NOTE: This is a proof-of-concept prototype for an authorized customer receiving an SO
 * from a reseller (that was previously the parent of this transferred customer).  Keep this
 * for reference when implementing peer-to-peer invitations.
 */
import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, Typography } from '@mui/material';

import LoadingSpinner from '../ui/LoadingSpinner';
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';

import AbEnvironment from '../auth/AbEnvironment';

import AuthorizeOrder from './AuthorizeOrder';

const abEnvironment = new AbEnvironment();
const SubscriptionOrderApproveQuery = graphql`
query SubscriptionOrderApproveQuery($accountId: UUID!, $userId: UUID!, $projectId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountName
    contactFirstName
    contactLastName
    contactEmail
    address1
    address2
    city
    state
    postalCode
    latestBudgetApproved
    autoTopupActive
    useBudgets
    initialBalanceAmount
    rAutoTopupAmount
    rAutoTopupThreshold
    rMaxMonthlyBudget
    rMaxCharge
    budgetApprovedCount
    rPreApproveSubs
    projectTsByCustomerAccountId(condition: {projectId: $projectId}) {
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        latestBudgetApproved
        accountTByResellerAccountId {
          accountId
      		accountName
      		contactFirstName
      		contactLastName
      		contactEmail
      		logo
        }
        walletTByCustomerWalletId {
          walletId
          initialBalanceAmount
          autoTopupAmount
          rAutoTopupAmount
          rAutoTopupThreshold
          autoTopupThreshold
          rMaxMonthlyBudget
          maxMonthlyBudget
          rMaxCharge
        }
      }
    }
    paymentSourceTsByAccountId(condition: {psStatus: 0}) {
      totalCount
      nodes {
        psType
        psStatus
      }
    }
    userTsByAccountId(condition:{userId: $userId}) {
    	totalCount
      nodes {
        userName
        termsAcceptDate
        userConfirmDate
      }
    }
    customerServiceTsByCustomerAccountId {
      totalCount
      nodes {
        customerServiceId
        appId
        customerAccountId
        serviceStatus
        appSkuPrice
        billingFrequency
        paymentOrder
        passThroughFees
        soAccepted
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
    customerAppTsByCustomerAccountId {
      totalCount
      nodes {
        customerAppId
        appId
        customerAccountId
        appStatus
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        passThroughFees
        bcId
        sharedEmail
        showSubscription
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
    customerServicesForProject: customerServiceTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerServiceId
        appId
        customerAccountId
        serviceStatus
        appSkuPrice
        billingFrequency
        paymentOrder
        passThroughFees
        soAccepted
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
    customerAppsForProject: customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerAppId
        appId
        customerAccountId
        appStatus
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        passThroughFees
        bcId
        sharedEmail
        showSubscription
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
  }
}
`;

const styles = theme => ({
  root: {
    // margin: "0 auto",
    //padding: "0 8px"
  }, 
});


class SubscriptionOrderApprove extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      
    };
  }
  
  render() {
    const { isAuthenticated, isAuthenticatedCustomer, needAssent, logError } = this.props.auth;

    const classes = this.props.classes;

    let accountId = abEnvironment.getUserAttribute('a_id');
    let userId = abEnvironment.getUserAttribute('u_id');
    let projectBased = false;
    let projectId = '00000000-0000-0000-0000-000000000000';  //default in case project not passed in.

    const params = new URLSearchParams(this.props.location.search);
    let step = params.get('step');
    let projectIdParm = params.get('projectId');
    if (projectIdParm) {
      projectBased = true;
      projectId = projectIdParm;
    }

    if (!isAuthenticated() || needAssent() || !isAuthenticatedCustomer()) {
      return (
        <Redirect to='/home' />
      );
    } else {

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={SubscriptionOrderApproveQuery}
          variables={{accountId: accountId, userId: userId, projectId: projectId}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              if (props.accountTByAccountId === null)
              {
                return (
                  <div className={classes.root}>
                    <PageTitle title={"Account not found"}/>
                  </div>
                );
              } else {
  
                //determine if this customer is project based.
                let project = null;
   
                if (projectBased) { 
                  //the query itself uses project id so it is always first one
                  project = props.accountTByAccountId.projectTsByCustomerAccountId.nodes[0];
                }
  
                //determine if this customer has completed this, if so, magic link should now go elsewhere
                //if they have not completed everything, certain steps drop off.
                let companyAddressPresent = false;
                if (props.accountTByAccountId.address1 !== null) {
                  companyAddressPresent = true;
                }
  
                let assent = false;
                let user = props.accountTByAccountId.userTsByAccountId.nodes[0];
                if (user.termsAcceptDate !== null && user.userConfirmDate !== null) {
                  assent = true
                }
            
                let paymentSources = props.accountTByAccountId.paymentSourceTsByAccountId.nodes;
            
                let achPresent = false;
                let ccPresent = false;
                //note the graph query is already pulling just active payment sources.
                for (let i = 0; i < paymentSources.length; i++) {
                  let paymentSource = paymentSources[i];
                  if (paymentSource.psType === 1) {
                    achPresent = true;
                  }
                  if (paymentSource.psType === 0) {
                    ccPresent = true;
                  }
             
                }
            
                let latestBudgetApproved = false;
                if (props.accountTByAccountId.useBudgets === 0) {
                  latestBudgetApproved = true;
                } else {
                  if (project) {
                    if (project.latestBudgetApproved === 1) {
                      latestBudgetApproved = true;
                    }
                  } else if (props.accountTByAccountId.latestBudgetApproved === 1) {
                    latestBudgetApproved = true;
                  }
                }
  
                /*let subscriptionsPresent = false;
                if (props.accountTByAccountId.customerAppTsByCustomerAccountId.totalCount > 0) {
                  subscriptionsPresent = true;
                }*/
                if (props.accountTByAccountId.useBudgets === 0) {
                  //The customer got an email to approve budgets but the reseller subsequently turned them off.
                  localStorage.setItem('fromstepperresumecustomer', true);
                  return (
                    <Redirect to={'/myapps'}  />
                  );
  
  
                } else if ((companyAddressPresent && assent && (achPresent || ccPresent)) && !step && latestBudgetApproved) {
                  //if user has done everything they need to do, bypass stepper and take then to myapps.
                  //this is not really true but it avoids another if clause in customerapplist (needed behavior is same)
                  localStorage.setItem('fromstepperresumecustomer', true);
                  return (
                    <Redirect to={'/myapps'}  />
                  );
                } else {
  
                  let theTitle = 'Authorize ' + project.accountTByResellerAccountId.accountName + ' to subscribe on your behalf';
  
                  return (
                    <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2}>
                      <Grid item xs={12}>
                        <Grid container direction="column" justifyContent="center" alignItems="center" spacing={1}>
                          <Grid item xs={12}>
                            <Typography variant="h4" align="center">
                              Let's set up your project!
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h6" align="center">
                              {theTitle}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                     <Grid item xs={12}>
                        <AuthorizeOrder auth={this.props.auth} project={project} account={props.accountTByAccountId} location={this.props.location} history={this.props.history}/>
                      </Grid>
                    </Grid>
                  );
                }
              }
            }
            return <LoadingSpinner />
          }}
        />
      );
    }

  }
}

export default withStyles(styles)(SubscriptionOrderApprove);